// ════════════════════════════════════════════════════════════════════════════
//  ManDana — reading a code with the camera
//  ----------------------------------------------------------------------------
//  A device joining an account has a code to get hold of, and typing twenty
//  characters read off another screen is the step people get wrong. So the code
//  is drawn as a QR at both ends (app/md-qr.ts), and this is the third way of
//  reading one: point this app's own camera at it.
//
//  THE THIRD WAY, DELIBERATELY. It is not the main one and it is not the floor:
//
//    · The OS camera app reads the same picture, because the picture carries a
//      URL. Nothing is installed, nothing is granted, and it works on every
//      phone made this decade.
//    · The code can be typed. That field never goes away.
//    · A screen with no camera shows a code instead and is approved from one
//      that has a session (app/md-pair.jsx).
//
//  So this file is an accelerator with three floors under it, and every failure
//  in it is silent by design: refused, absent, held by another app, or a browser
//  with no decoder all leave the person exactly where they were.
//
//  NO DECODER IS SHIPPED, AND THAT IS THE DESIGN, NOT AN OMISSION. Reading a QR
//  in JavaScript costs about 57 KB gzipped, on every boot, for everybody. What it
//  buys is the in-app camera on Safari, which has no BarcodeDetector — and Safari
//  is precisely where the OS camera app is the better path anyway, because it is
//  two taps and needs no permission from us at all. So `mdScanSupported()` is
//  asked BEFORE the button is drawn, and a browser that cannot decode is never
//  offered a camera it would have to apologise for.
//
//  Exposes mdScanCode, mdScanSupported, mdScanStart.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  /* Four frames a second. A QR fills the viewport and holds still — somebody is
     pointing a phone at a screen — so the thing that decides how fast this feels
     is how quickly they get the camera aimed, not how often it is asked. Every
     frame beyond that is a full-resolution decode nobody was waiting for, on the
     battery of a device that has not signed in yet. */
  const MD_SCAN_INTERVAL_MS = 250;

  /**
   * The code inside a scanned payload, or null.
   *
   * Three shapes arrive here. Our own URL, which is what the QR carries and what
   * the OS camera app opens. A bare code, which is what somebody typing into
   * another QR generator produces. And everything else in the world, because a
   * camera pointed at a room finds posters, WiFi codes and shop windows.
   *
   * A `?grant=` URL is REFUSED rather than unwrapped. It is the other end of this
   * same flow — a code asking to be approved, which can never buy a session — and
   * handing it to the door would spend the person's press on a refusal that was
   * knowable from the payload.
   */
  function mdScanCode(payload: unknown): string | null {
    if (typeof payload !== 'string') return null;
    const text = payload.trim();
    if (!text) return null;

    if (/^https?:\/\//i.test(text)) {
      try {
        const here = new URL(text);
        /* Only the link parameter. mdLandingLinkParam answers null for a grant
           URL and for anything else with a query string, which is the refusal
           above stated once, in the file that owns both names. */
        return g.mdLandingLinkParam ? g.mdLandingLinkParam(here.search) : null;
      } catch (_) { return null; }
    }

    /* A bare payload is only a code if it could be one. The alphabet is what
       every code this app mints is drawn from (server/auth.js) plus the dashes
       that group it — which is enough to leave a WiFi string or a phone number
       alone without pretending to validate a credential the server will check. */
    return /^[A-Za-z0-9-]{4,64}$/.test(text) ? text : null;
  }

  /**
   * Can this browser read a code at all?
   *
   * Asked before the camera is offered, not after it fails. Both halves have to
   * be there: something to open the camera with, and something to decode with.
   */
  function mdScanSupported(): boolean {
    const media = g.navigator && g.navigator.mediaDevices;
    return typeof g.BarcodeDetector === 'function'
      && !!media && typeof media.getUserMedia === 'function';
  }

  /** What getUserMedia's refusals mean to somebody holding the device. */
  function reason(name: unknown): string {
    switch (name) {
      case 'NotAllowedError': case 'SecurityError': return 'denied';
      case 'NotFoundError': case 'DevicesNotFoundError': case 'OverconstrainedError': return 'no_camera';
      case 'NotReadableError': case 'TrackStartError': return 'in_use';
      default: return 'failed';
    }
  }

  /** Every track, off. Called from stop() and from a successful read alike. */
  function release(stream: any, video: any): void {
    try {
      const tracks = stream && typeof stream.getTracks === 'function' ? stream.getTracks() : [];
      for (const track of tracks) { try { track.stop(); } catch (_) {} }
    } catch (_) {}
    /* The element keeps a reference to the stream, and a browser holds the
       camera warm for an element that still points at one — the indicator light
       stays on. Clearing it is part of stopping, not tidying up after it. */
    try { if (video) video.srcObject = null; } catch (_) {}
  }

  /**
   * Open the camera, watch for a code, and hand back a way to stop.
   *
   * `{ok: true, stop}` when the camera opened. `{ok: false, error}` otherwise,
   * and never a throw: every caller of this is a surface with a typed field
   * still standing behind it, so a refusal is a state to draw, not an exception
   * to handle.
   *
   * `onCode` is called AT MOST ONCE and the camera is released before it runs.
   * One code is the whole answer to the question the panel is asking, and a
   * second read would redeem a code under the session the first one bought.
   */
  async function mdScanStart(video: any, onCode: unknown): Promise<any> {
    if (!mdScanSupported()) return { ok: false, error: 'unsupported' };
    if (!video || typeof video.play !== 'function' || typeof onCode !== 'function') {
      return { ok: false, error: 'failed' };
    }

    let stream: any = null;
    try {
      stream = await g.navigator.mediaDevices.getUserMedia({
        /* A preference, not a demand: `exact` would fail outright on a laptop,
           which has one camera and it faces the person. */
        video: { facingMode: 'environment' },
        audio: false,
      });
    } catch (e: any) {
      return { ok: false, error: reason(e && e.name) };
    }

    /* iOS plays a video that is not inline and not muted FULL SCREEN, with its
       own controls, over whatever panel asked for it. Both have to be set before
       the stream is attached and long before play(): afterwards the video is
       already in the wrong place. */
    try {
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.muted = true;
    } catch (_) {
      /* An element that will not take the attributes still decodes. The cost is
         a full-screen player on iOS, which is ugly and not a reason to refuse. */
    }

    /* THIS one is fatal. Without the stream on the element there are no frames to
       decode, so `ok: true` would be a promise of a scan that cannot happen. */
    try {
      video.srcObject = stream;
    } catch (_) {
      release(stream, video);
      return { ok: false, error: 'failed' };
    }

    try {
      await video.play();
    } catch (_) {
      /* A refused play() is not fatal — autoplay policy stops the presentation,
         not the frames, and the decoder reads the element rather than what the
         person can see. */
    }

    let detector: any;
    try {
      detector = new g.BarcodeDetector({ formats: ['qr_code'] });
    } catch (e: any) {
      release(stream, video);
      return { ok: false, error: 'unsupported' };
    }

    let alive = true;
    let timer: any = 0;

    const stop = () => {
      if (!alive) return;
      alive = false;
      if (timer) { clearTimeout(timer); timer = 0; }
      release(stream, video);
    };

    const look = async () => {
      if (!alive) return;
      let found: string | null = null;
      try {
        const marks = await detector.detect(video);
        for (const mark of (Array.isArray(marks) ? marks : [])) {
          const code = mdScanCode(mark && mark.rawValue);
          if (code) { found = code; break; }
        }
      } catch (_) {
        /* A frame that could not be decoded — the video has no dimensions yet,
           or the decoder was handed a blank one. The next frame is the answer to
           this, not an error message. */
      }
      if (!alive) return;
      if (found) { stop(); (onCode as (code: string) => void)(found); return; }
      timer = setTimeout(look, MD_SCAN_INTERVAL_MS);
    };
    timer = setTimeout(look, 0);

    return { ok: true, stop };
  }

  const published = { mdScanCode, mdScanSupported, mdScanStart, MD_SCAN_INTERVAL_MS };
  Object.assign(g, published);

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = published;
})(typeof window !== 'undefined' ? window : globalThis);
